import React, { useEffect, useState } from 'react';
import { Grid, TextField, Button, MenuItem, Select, FormControl, InputLabel, Collapse, IconButton, Typography } from '@material-ui/core';
import { FilterList, ExpandLess, ExpandMore } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import SearchSelect from 'react-select';
import service from '../../apis/service';
import { apiEndpoints } from '../../constants/apiEndpoints';
import { ErrorToast } from '../utils/Utils';

const useStyles = makeStyles({
  filterWrap: {
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    marginBottom: '12px',
    border: '1px solid #eee',
    overflow: 'hidden',
  },
  filterHeader: {
    padding: '8px 16px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    userSelect: 'none',
  },
  filterBody: {
    padding: '12px 16px 16px',
    borderTop: '1px solid #eee',
  },
  label: {
    fontSize: '12px',
    color: 'rgba(0,0,0,0.55)',
    marginBottom: '4px',
    display: 'block',
  },
  applyBtn: {
    backgroundColor: '#622FEE',
    color: '#fff',
    '&:hover': { backgroundColor: '#4a1fc0' },
    marginRight: '8px',
  },
  clearBtn: {
    color: 'rgba(0,0,0,0.6)',
  },
});

const EMPTY = {
  name: '',
  city: '',
  state: '',
  country: '',
  source: '',
  enabled: '',
  leadPipelineId: [],
  accountOwnerHash: [],
  segmentId: [],
  accountTypeId: [],
  locationTypeId: [],
  launchMilestoneId: [],
  lifeCycleStageId: [],
  organizationSizeId: [],
};

const DealershipListFilters = ({ onApply, onClear }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [opts, setOpts] = useState({
    pipelines: [],
    owners: [],
    segments: [],
    accountTypes: [],
    locationTypes: [],
    launchMilestones: [],
    lifeCycleStages: [],
    organizationSizes: [],
  });

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [pipeline, owners, master] = await Promise.all([
          service.get(apiEndpoints.DEALER_SALES_PIPELINE),
          service.get(apiEndpoints.DEALER_PX_OWNERS),
          service.get(apiEndpoints.DEALER_MASTER_CEANUP_DROPDOWNS),
        ]);
        setOpts({
          pipelines: (pipeline || []).map((p) => ({ value: p.id, label: p.title })),
          owners: (owners || []).map((o) => ({ value: o.hash, label: o.name })),
          segments: master?.segments || [],
          accountTypes: master?.accountTypes || [],
          locationTypes: master?.locationTypes || [],
          launchMilestones: master?.launchMilestones || [],
          lifeCycleStages: master?.lifeCycleStages || [],
          organizationSizes: master?.organizationSizes || [],
        });
      } catch (err) {
        ErrorToast(err);
      }
    };
    fetchOptions();
  }, []);

  const setText = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  const setMulti = (key) => (selected) => setForm((p) => ({ ...p, [key]: selected || [] }));

  const buildPayload = () => {
    const f = {};
    if (form.name) f.name = form.name;
    if (form.city) f.city = form.city;
    if (form.state) f.state = form.state;
    if (form.country) f.country = form.country;
    if (form.source) f.source = form.source;
    if (form.enabled !== '') f.enabled = form.enabled === 'true';
    if (form.leadPipelineId.length) f.leadPipelineId = form.leadPipelineId.map((o) => o.value);
    if (form.accountOwnerHash.length) f.accountOwnerHash = form.accountOwnerHash.map((o) => o.value);
    if (form.segmentId.length) f.segmentId = form.segmentId.map((o) => o.value);
    if (form.accountTypeId.length) f.accountTypeId = form.accountTypeId.map((o) => o.value);
    if (form.locationTypeId.length) f.locationTypeId = form.locationTypeId.map((o) => o.value);
    if (form.launchMilestoneId.length) f.launchMilestoneId = form.launchMilestoneId.map((o) => o.value);
    if (form.lifeCycleStageId.length) f.lifeCycleStageId = form.lifeCycleStageId.map((o) => o.value);
    if (form.organizationSizeId.length) f.organizationSizeId = form.organizationSizeId.map((o) => o.value);
    return f;
  };

  const handleApply = () => onApply(buildPayload());

  const handleClear = () => {
    setForm(EMPTY);
    onClear();
  };

  const multiSelectStyles = {
    control: (base) => ({ ...base, minHeight: 38, fontSize: 13 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
    valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
    multiValue: (base) => ({ ...base, maxWidth: 90 }),
  };

  return (
    <div className={classes.filterWrap}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.filterHeader} onClick={() => setOpen((p) => !p)}>
        <Grid container alignItems="center" spacing={1}>
          <Grid item><FilterList style={{ color: '#622FEE', fontSize: 18 }} /></Grid>
          <Grid item><Typography style={{ fontSize: '14px', fontWeight: 600 }}>Filters</Typography></Grid>
        </Grid>
        <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
      </div>

      <Collapse in={open}>
        <div className={classes.filterBody}>
      <Grid container spacing={2}>
        {/* Text filters */}
        <Grid item xs={12} sm={6} md={2}>
          <span className={classes.label}>Name</span>
          <TextField value={form.name} onChange={setText('name')} fullWidth size="small" variant="outlined" placeholder="Search name" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <span className={classes.label}>City</span>
          <TextField value={form.city} onChange={setText('city')} fullWidth size="small" variant="outlined" placeholder="City" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <span className={classes.label}>State</span>
          <TextField value={form.state} onChange={setText('state')} fullWidth size="small" variant="outlined" placeholder="State" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <span className={classes.label}>Country</span>
          <TextField value={form.country} onChange={setText('country')} fullWidth size="small" variant="outlined" placeholder="Country" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <span className={classes.label}>Source</span>
          <TextField value={form.source} onChange={setText('source')} fullWidth size="small" variant="outlined" placeholder="Source" />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small" variant="outlined">
            <InputLabel>Enabled</InputLabel>
            <Select value={form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.value }))} label="Enabled">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Multi-select filters */}
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Lead Pipeline</span>
          <SearchSelect isMulti options={opts.pipelines} value={form.leadPipelineId} onChange={setMulti('leadPipelineId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Account Owner</span>
          <SearchSelect isMulti options={opts.owners} value={form.accountOwnerHash} onChange={setMulti('accountOwnerHash')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Segment</span>
          <SearchSelect isMulti options={opts.segments} value={form.segmentId} onChange={setMulti('segmentId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Account Type</span>
          <SearchSelect isMulti options={opts.accountTypes} value={form.accountTypeId} onChange={setMulti('accountTypeId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Location Type</span>
          <SearchSelect isMulti options={opts.locationTypes} value={form.locationTypeId} onChange={setMulti('locationTypeId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Launch Milestone</span>
          <SearchSelect isMulti options={opts.launchMilestones} value={form.launchMilestoneId} onChange={setMulti('launchMilestoneId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Life Cycle Stage</span>
          <SearchSelect isMulti options={opts.lifeCycleStages} value={form.lifeCycleStageId} onChange={setMulti('lifeCycleStageId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <span className={classes.label}>Org Size</span>
          <SearchSelect isMulti options={opts.organizationSizes} value={form.organizationSizeId} onChange={setMulti('organizationSizeId')} styles={multiSelectStyles} placeholder="All" />
        </Grid>

        {/* Buttons */}
        <Grid item xs={12}>
          <Button variant="contained" className={classes.applyBtn} onClick={handleApply} size="small">
            Apply Filters
          </Button>
          <Button className={classes.clearBtn} onClick={handleClear} size="small">
            Clear
          </Button>
        </Grid>
      </Grid>
        </div>
      </Collapse>
    </div>
  );
};

export default DealershipListFilters;
